import React, { useState, useEffect } from 'react';
import { PendingApproval } from '../../services/approvals';
import './ImageModal.css';

interface ImageModalProps {
  approval: PendingApproval;
  onClose: () => void;
  onApprove: (approval: PendingApproval, platforms: string[]) => void;
  onReject: (approval: PendingApproval, reason: string) => void;
  onNext?: () => void;
  onPrevious?: () => void;
  hasNext?: boolean;
  hasPrevious?: boolean;
}

const ImageModal: React.FC<ImageModalProps> = ({
  approval,
  onClose,
  onApprove,
  onReject,
  onNext,
  onPrevious,
  hasNext = false,
  hasPrevious = false,
}) => {
  const [activeTab, setActiveTab] = useState<'details' | 'metadata' | 'history'>('details');
  const [editedMetadata, setEditedMetadata] = useState({
    title: approval.title || '',
    description: approval.description || '',
    keywords: approval.keywords || [],
    category: approval.category || '',
  });
  const [newKeyword, setNewKeyword] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>(
    approval.selected_platforms || ['shutterstock', 'alamy', 'pond5', 'depositphotos']
  );
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);

  const platforms = [
    { id: 'shutterstock', name: 'Shutterstock', color: '#EE2A24' },
    { id: 'alamy', name: 'Alamy', color: '#00A859' },
    { id: 'pond5', name: 'Pond5', color: '#FF6B35' },
    { id: 'depositphotos', name: 'Depositphotos', color: '#5B9BD5' },
    { id: 'getty', name: 'Getty Images', color: '#000000' },
    { id: 'adobe', name: 'Adobe Stock', color: '#FF0000' },
  ];

  const rejectionReasons = [
    'Quality not sufficient',
    'Contains copyrighted material',
    'Contains recognizable people',
    'Contains text or logos',
    'Poor composition',
    'Overexposed/Underexposed',
    'Motion blur',
    'Duplicate content',
    'Not suitable for stock',
  ];

  useEffect(() => {
    // Update metadata when approval changes
    setEditedMetadata({
      title: approval.title || '',
      description: approval.description || '',
      keywords: approval.keywords || [],
      category: approval.category || '',
    });
  }, [approval]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft' && hasPrevious && onPrevious) {
        onPrevious();
      } else if (e.key === 'ArrowRight' && hasNext && onNext) {
        onNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onNext, onPrevious, hasNext, hasPrevious]);

  const handleAddKeyword = () => {
    if (newKeyword.trim() && !editedMetadata.keywords.includes(newKeyword.trim())) {
      setEditedMetadata({
        ...editedMetadata,
        keywords: [...editedMetadata.keywords, newKeyword.trim()],
      });
      setNewKeyword('');
    }
  };

  const handleRemoveKeyword = (keyword: string) => {
    setEditedMetadata({
      ...editedMetadata,
      keywords: editedMetadata.keywords.filter(k => k !== keyword),
    });
  };

  const togglePlatform = (platformId: string) => {
    setSelectedPlatforms(prev =>
      prev.includes(platformId)
        ? prev.filter(p => p !== platformId)
        : [...prev, platformId]
    );
  };

  const handleApprove = () => {
    // Update approval with edited metadata before approving
    const updatedApproval = {
      ...approval,
      ...editedMetadata,
      selected_platforms: selectedPlatforms,
    };
    onApprove(updatedApproval, selectedPlatforms);
  };

  const handleReject = () => {
    if (rejectReason.trim()) {
      onReject(approval, rejectReason);
      setShowRejectDialog(false);
      setRejectReason('');
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'Unknown';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const getQualityColor = (score?: number) => {
    if (!score) return 'var(--color-gray-400)';
    if (score >= 80) return 'var(--color-success)';
    if (score >= 60) return 'var(--color-warning)';
    return 'var(--color-danger)';
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <h2>{editedMetadata.title || approval.original_filename || 'Untitled Image'}</h2>
          <button className="modal-close" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Image Navigation */}
        <div className="modal-body">
          <div className="image-viewer">
            {hasPrevious && onPrevious && (
              <button className="nav-button nav-prev" onClick={onPrevious}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
                </svg>
              </button>
            )}

            <div className="image-container">
              <img
                src={approval.full_url || approval.preview_url || `/api/approvals/${approval.id}/preview`}
                alt={approval.title || 'Stock image'}
                className="modal-image"
              />

              {/* Quality Badge */}
              <div
                className="quality-badge-large"
                style={{ backgroundColor: getQualityColor(approval.quality_score) }}
              >
                Quality Score: {approval.quality_score || 'N/A'}
              </div>
            </div>

            {hasNext && onNext && (
              <button className="nav-button nav-next" onClick={onNext}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8.59 16.59L10 18l6-6-6-6-1.41 1.41L13.17 12z"/>
                </svg>
              </button>
            )}
          </div>

          {/* Tabs */}
          <div className="modal-tabs">
            <button
              className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Details
            </button>
            <button
              className={`tab-button ${activeTab === 'metadata' ? 'active' : ''}`}
              onClick={() => setActiveTab('metadata')}
            >
              Metadata
            </button>
            <button
              className={`tab-button ${activeTab === 'history' ? 'active' : ''}`}
              onClick={() => setActiveTab('history')}
            >
              History
            </button>
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {activeTab === 'details' && (
              <div className="details-tab">
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>File Name</label>
                    <span>{approval.original_filename || 'Unknown'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Resolution</label>
                    <span>{approval.resolution || 'Unknown'}</span>
                  </div>
                  <div className="detail-item">
                    <label>File Size</label>
                    <span>{formatFileSize(approval.file_size_bytes)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Format</label>
                    <span>{approval.file_format || 'JPEG'}</span>
                  </div>
                  <div className="detail-item">
                    <label>Uploaded</label>
                    <span>{new Date(approval.created_at).toLocaleString()}</span>
                  </div>
                  <div className="detail-item">
                    <label>Batch ID</label>
                    <span>{approval.batch_id || 'N/A'}</span>
                  </div>
                </div>

                {/* Platform Selection */}
                <div className="platform-selection">
                  <h3>Select Platforms for Upload</h3>
                  <div className="platform-grid">
                    {platforms.map(platform => (
                      <label key={platform.id} className="platform-option">
                        <input
                          type="checkbox"
                          checked={selectedPlatforms.includes(platform.id)}
                          onChange={() => togglePlatform(platform.id)}
                        />
                        <span
                          className="platform-label"
                          style={{ borderColor: selectedPlatforms.includes(platform.id) ? platform.color : 'transparent' }}
                        >
                          {platform.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'metadata' && (
              <div className="metadata-tab">
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={editedMetadata.title}
                    onChange={(e) => setEditedMetadata({ ...editedMetadata, title: e.target.value })}
                    placeholder="Enter image title"
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={editedMetadata.description}
                    onChange={(e) => setEditedMetadata({ ...editedMetadata, description: e.target.value })}
                    placeholder="Enter image description"
                    rows={4}
                    className="form-textarea"
                  />
                </div>

                <div className="form-group">
                  <label>Category</label>
                  <select
                    value={editedMetadata.category}
                    onChange={(e) => setEditedMetadata({ ...editedMetadata, category: e.target.value })}
                    className="form-select"
                  >
                    <option value="">Select category</option>
                    <option value="nature">Nature</option>
                    <option value="business">Business</option>
                    <option value="technology">Technology</option>
                    <option value="lifestyle">Lifestyle</option>
                    <option value="travel">Travel</option>
                    <option value="food">Food</option>
                    <option value="abstract">Abstract</option>
                    <option value="architecture">Architecture</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Keywords</label>
                  <div className="keyword-input-group">
                    <input
                      type="text"
                      value={newKeyword}
                      onChange={(e) => setNewKeyword(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleAddKeyword()}
                      placeholder="Add keyword"
                      className="form-input"
                    />
                    <button onClick={handleAddKeyword} className="btn btn-primary">
                      Add
                    </button>
                  </div>
                  <div className="keyword-list">
                    {editedMetadata.keywords.map((keyword, index) => (
                      <span key={index} className="keyword-chip">
                        {keyword}
                        <button
                          className="keyword-remove"
                          onClick={() => handleRemoveKeyword(keyword)}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'history' && (
              <div className="history-tab">
                <div className="history-timeline">
                  <div className="history-item">
                    <div className="history-icon">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M8 0a8 8 0 110 16A8 8 0 018 0zM7 11.5v-6l4 3-4 3z"/>
                      </svg>
                    </div>
                    <div className="history-content">
                      <p className="history-title">Image Uploaded</p>
                      <p className="history-date">{new Date(approval.created_at).toLocaleString()}</p>
                    </div>
                  </div>

                  {approval.ai_analysis_at && (
                    <div className="history-item">
                      <div className="history-icon">
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                          <path d="M8 4a.5.5 0 01.5.5v3h3a.5.5 0 010 1h-3.5a.5.5 0 01-.5-.5v-3.5A.5.5 0 018 4z"/>
                          <path d="M8 1a7 7 0 110 14A7 7 0 018 1zm0 1a6 6 0 100 12A6 6 0 008 2z"/>
                        </svg>
                      </div>
                      <div className="history-content">
                        <p className="history-title">AI Analysis Complete</p>
                        <p className="history-date">{new Date(approval.ai_analysis_at).toLocaleString()}</p>
                      </div>
                    </div>
                  )}

                  <div className="history-item current">
                    <div className="history-icon">
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                        <path d="M8 0a8 8 0 110 16A8 8 0 018 0zm0 2a6 6 0 100 12A6 6 0 008 2z"/>
                        <circle cx="8" cy="8" r="2"/>
                      </svg>
                    </div>
                    <div className="history-content">
                      <p className="history-title">Awaiting Review</p>
                      <p className="history-date">Current Status</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <button onClick={() => setShowRejectDialog(true)} className="btn btn-danger">
            Reject Image
          </button>
          <button onClick={handleApprove} className="btn btn-success">
            Approve & Upload to {selectedPlatforms.length} Platform{selectedPlatforms.length !== 1 ? 's' : ''}
          </button>
        </div>

        {/* Reject Dialog */}
        {showRejectDialog && (
          <div className="reject-dialog">
            <div className="reject-dialog-content">
              <h3>Reject Image</h3>
              <p>Please select or enter a reason for rejection:</p>

              <div className="reject-reasons">
                {rejectionReasons.map(reason => (
                  <button
                    key={reason}
                    className={`reason-chip ${rejectReason === reason ? 'selected' : ''}`}
                    onClick={() => setRejectReason(reason)}
                  >
                    {reason}
                  </button>
                ))}
              </div>

              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Enter custom reason..."
                rows={3}
                className="form-textarea"
              />

              <div className="reject-dialog-footer">
                <button onClick={() => setShowRejectDialog(false)} className="btn btn-ghost">
                  Cancel
                </button>
                <button onClick={handleReject} className="btn btn-danger" disabled={!rejectReason.trim()}>
                  Confirm Rejection
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageModal;